/**
 * Admin Intakes RPC definitions
 *
 * The intake acquisition record + magic-link lifecycle RpcGroup the cockpit
 * consumes. `list`/`get` index and inspect the funnel; `create`/`update` manage
 * the worksheet; `finalize`/`regenerateLink`/`abandon` drive the
 * customer magic-link lifecycle. All endpoints require admin role.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { CloudProvider, DeploymentType, HetznerLocation } from "@fm/db/models/AgentInstallation";
import { CustomerType } from "@fm/db/models/Customer";
import { Intake, IntakeSource, IntakeStatus } from "@fm/db/models/Intake";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import {
  ClerkAllowlistError,
  DatabaseError,
  Forbidden,
  IntakeNotEditableError,
  PersonAlreadyExistsError,
  RecordNotFoundError,
  Unauthorized,
  ValidationError,
} from "../errors.ts";

// ============================================================================
// Response Schemas
// ============================================================================

/**
 * A `list` row — the intake plus the days it has spent in its current stage.
 *
 * @since 1.0.0
 */
export const IntakeListItem = Schema.Struct({
  intake: Intake.json,
  daysInStage: Schema.Number,
});

/**
 * Computed progress signals for a single intake — booleans/counts only, never
 * the plaintext magic-link URL.
 *
 * @since 1.0.0
 */
export const IntakeProgress = Schema.Struct({
  hasLink: Schema.Boolean,
  linkExpired: Schema.Boolean,
  linkResolved: Schema.Boolean,
  channelsSelected: Schema.Number,
  connectorsSelected: Schema.Number,
  llmSelected: Schema.Boolean,
  deploymentSelected: Schema.Boolean,
});

/**
 * The `get` detail shape — the intake plus its progress signals. `linkUrl` is
 * always `null`: the plaintext magic-link token is never stored at rest (only
 * its hash is), so it cannot be re-derived. Kept `NullOr` for back-compat; a
 * live link is re-viewed via `regenerateLink`.
 *
 * @since 1.0.0
 */
export const IntakeDetail = Schema.Struct({
  intake: Intake.json,
  progress: IntakeProgress,
  linkUrl: Schema.NullOr(Schema.String),
});

/**
 * The `finalize` result — the updated intake plus the plaintext magic-link URL.
 * `url` carries the freshly minted link on the first call and `null` on an
 * idempotent repeat.
 *
 * @since 1.0.0
 */
export const FinalizeResult = Schema.Struct({
  intake: Intake.json,
  url: Schema.NullOr(Schema.String),
  // The persisted contact email on the intake's primary person. Surfaced so the
  // worksheet's minted-link panel renders the address the link actually binds
  // to, never the operator's unsaved client-side value (which can silently
  // diverge from the DB once the person is Clerk-bound).
  personEmail: Schema.NullOr(Schema.String),
});

/**
 * The `regenerateLink` result — the updated intake plus the freshly minted
 * plaintext magic-link URL (always present).
 *
 * @since 1.0.0
 */
export const RegenerateResult = Schema.Struct({
  intake: Intake.json,
  url: Schema.String,
});

// ============================================================================
// Request Schemas
// ============================================================================

/**
 * Input for `list` — an optional lifecycle-stage filter.
 *
 * @since 1.0.0
 */
export const IntakeListInput = Schema.Struct({
  stage: Schema.optional(IntakeStatus),
});

/**
 * Input for `get` / `finalize` / `regenerateLink` / `abandon` — the intake id.
 *
 * @since 1.0.0
 */
export const IntakeByIdInput = Schema.Struct({
  id: Schema.NonEmptyTrimmedString,
});

/**
 * Input for `create` — the new owner person + customer identity (reused by
 * `createWithPerson`) plus the intake's acquisition channel and any pre-selected
 * wizard fields. The intake is always seeded `draft`.
 *
 * @since 1.0.0
 */
export const IntakeCreateInput = Schema.Struct({
  person: Schema.Struct({
    name: Schema.NonEmptyTrimmedString,
    email: Schema.NonEmptyTrimmedString,
    phone: Schema.optional(Schema.NullOr(Schema.String)),
  }),
  customer: Schema.Struct({
    type: CustomerType,
    name: Schema.NonEmptyTrimmedString,
    resellerId: Schema.optional(Schema.NullOr(Schema.String)),
  }),
  source: IntakeSource,
  sourceDetail: Schema.optional(Schema.NullOr(Schema.String)),
  createdByPersonId: Schema.optional(Schema.NullOr(Schema.String)),
  selectedChannels: Schema.optional(Schema.Array(Schema.String)),
  selectedConnectors: Schema.optional(Schema.Array(Schema.String)),
  selectedLlm: Schema.optional(Schema.NullOr(Schema.String)),
  deploymentType: Schema.optional(Schema.NullOr(DeploymentType)),
  cloudProvider: Schema.optional(Schema.NullOr(CloudProvider)),
  // The CSR-selected cloud-vps region (Hetzner location slug), only meaningful
  // for a cloud-vps intake. Validated server-side against the supported set.
  region: Schema.optional(Schema.NullOr(HetznerLocation)),
});

/**
 * Input for `ensureForCustomer` — find-or-create the (1:1) intake for an
 * EXISTING customer. `agentId`, when present, attaches that already-provisioned
 * agent to the intake (`agent_installations.intake_id`) so `onboarding/state`
 * resolves it; omitted means no attach.
 *
 * @since 1.0.0
 */
export const IntakeEnsureForCustomerInput = Schema.Struct({
  customerId: Schema.NonEmptyTrimmedString,
  agentId: Schema.optional(Schema.String),
});
export type IntakeEnsureForCustomerInput = Schema.Schema.Type<typeof IntakeEnsureForCustomerInput>;

/**
 * Input for `update` — the intake id plus any subset of the mutable wizard
 * content fields. Status and link fields are owned by the lifecycle endpoints,
 * not editable here.
 *
 * @since 1.0.0
 */
export const IntakeUpdateInput = Schema.Struct({
  id: Schema.NonEmptyTrimmedString,
  primaryPersonId: Schema.optional(Schema.NullOr(Schema.String)),
  createdByPersonId: Schema.optional(Schema.NullOr(Schema.String)),
  // The contact person's identity fields. When present, they are propagated onto
  // the intake's primary person — but ONLY while the intake is pre-resolution
  // (draft/sent) and the person is not yet Clerk-bound. After sign-up the email
  // is a verified identity and this patch is ignored server-side.
  person: Schema.optional(Schema.Struct({
    name: Schema.NonEmptyTrimmedString,
    email: Schema.NonEmptyTrimmedString,
    phone: Schema.optional(Schema.NullOr(Schema.String)),
  })),
  source: Schema.optional(IntakeSource),
  sourceDetail: Schema.optional(Schema.NullOr(Schema.String)),
  selectedChannels: Schema.optional(Schema.Array(Schema.String)),
  selectedConnectors: Schema.optional(Schema.Array(Schema.String)),
  selectedLlm: Schema.optional(Schema.NullOr(Schema.String)),
  deploymentType: Schema.optional(Schema.NullOr(DeploymentType)),
  cloudProvider: Schema.optional(Schema.NullOr(CloudProvider)),
  // The CSR-selected cloud-vps region (Hetzner location slug); null clears it.
  // Only meaningful for a cloud-vps intake; validated server-side.
  region: Schema.optional(Schema.NullOr(HetznerLocation)),
  // The negotiated one-time install fee, in cents (null clears it). Persisted
  // onto the INTAKE (not the customer — that holds the recurring agent rate).
  installFeeAmount: Schema.optional(Schema.NullOr(Schema.Int)),
  notes: Schema.optional(Schema.NullOr(Schema.String)),
});

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Admin Intakes RPC group.
 *
 * All endpoints require admin role via AdminMiddleware.
 *
 * @since 1.0.0
 */
export class AdminIntakesRpcs extends RpcGroup.make(
  Rpc.make("list", {
    payload: IntakeListInput,
    success: Schema.Array(IntakeListItem),
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("get", {
    payload: IntakeByIdInput,
    success: IntakeDetail,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("create", {
    payload: IntakeCreateInput,
    success: Intake.json,
    error: Schema.Union(Unauthorized, Forbidden, ValidationError, PersonAlreadyExistsError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("ensureForCustomer", {
    payload: IntakeEnsureForCustomerInput,
    success: Intake.json,
    error: Schema.Union(Unauthorized, Forbidden, ValidationError, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("update", {
    payload: IntakeUpdateInput,
    success: Intake.json,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      ValidationError,
      IntakeNotEditableError,
      RecordNotFoundError,
      DatabaseError,
    ),
  }).middleware(AdminMiddleware),
  Rpc.make("finalize", {
    payload: IntakeByIdInput,
    success: FinalizeResult,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      ValidationError,
      RecordNotFoundError,
      DatabaseError,
      ClerkAllowlistError,
    ),
  }).middleware(AdminMiddleware),
  Rpc.make("regenerateLink", {
    payload: IntakeByIdInput,
    success: RegenerateResult,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      ValidationError,
      RecordNotFoundError,
      DatabaseError,
      ClerkAllowlistError,
    ),
  }).middleware(AdminMiddleware),
  Rpc.make("abandon", {
    payload: IntakeByIdInput,
    success: Intake.json,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
).prefix("admin/intakes/") {}
